import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { Button } from "~/ui-rtk/components/ui/controls"
import {
  MAX_ANALYZE_ADSETS,
  MAX_SIMILARITY_ADSETS,
  exceedsAdsetCap,
} from "../constants"
import type { CreativePlaybook } from "../types"

const PLAYBOOK_BUTTONS: Array<{ playbook: CreativePlaybook; label: string }> = [
  { playbook: "UNDERPERFORMERS", label: "Archive Underperformers" },
  // Ellipsis: opens the dead products / ended sales inputs modal first
  { playbook: "IRRELEVANT", label: "Archive Irrelevant Ads…" },
  { playbook: "DUPLICATIVE_LOSERS", label: "Archive Duplicates" },
  { playbook: "TOO_SIMILAR", label: "Review Similar Ads" },
]

interface ActionBarProps {
  selectedCount: number
  /** The playbook whose analysis is in flight — disables all buttons and
   *  spins the active one */
  analyzingPlaybook: CreativePlaybook | null
  onRun: (playbook: CreativePlaybook) => void
  onCancel: () => void
}

export default function ActionBar({
  selectedCount,
  analyzingPlaybook,
  onRun,
  onCancel,
}: ActionBarProps) {
  if (selectedCount === 0) return null

  // Backend caps: 50 ad sets per analyze run, 15 for similarity playbooks.
  let capHint: string | null = null
  if (selectedCount > MAX_ANALYZE_ADSETS) {
    capHint = `${selectedCount} ad sets selected — playbooks run on at most ${MAX_ANALYZE_ADSETS}`
  } else if (selectedCount > MAX_SIMILARITY_ADSETS) {
    capHint = `Similarity playbooks run on at most ${MAX_SIMILARITY_ADSETS} ad sets`
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-system-blue-800 border-t border-basic-blue px-6 py-4">
      <div className="max-w-screen-xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-white text-sm">
            {selectedCount} {selectedCount === 1 ? "ad set" : "ad sets"}{" "}
            selected
          </span>
          {capHint && (
            <span className="flex items-center gap-1.5">
              <ExclamationTriangleIcon className="w-4 h-4 text-yellow-400 shrink-0" />
              <span className="text-xs text-yellow-300">{capHint}</span>
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant={{ variant: "outlined" }}
            size="sm"
            disabled={analyzingPlaybook !== null}
            onClick={onCancel}
          >
            Cancel
          </Button>
          {PLAYBOOK_BUTTONS.map(({ playbook, label }) => (
            <Button
              key={playbook}
              variant={{ variant: "solid", color: "blue" }}
              size="sm"
              disabled={
                analyzingPlaybook !== null ||
                exceedsAdsetCap(playbook, selectedCount)
              }
              isLoading={analyzingPlaybook === playbook}
              onClick={() => onRun(playbook)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  )
}
